// apps/client/src/world/items.ts (M1-T13) — the 'items' layer of the OutdoorScene: dropped items on their tile, yard furniture on
// its footprint. Created on the first `out` snapshot by net/handlers/items.ts; room items are M1-T16's.
import * as THREE from 'three';
import type { ItemId, ItemView, TileXY, Vec2 } from '@whiskerton/shared';
import { footprintOf } from '@whiskerton/shared/rules/placement';
import type { AssetService } from '../assets/service';
import type { Game } from '../game/game';

export interface ItemsLayer {
  sync(items: readonly ItemView[]): Promise<void>; add(item: ItemView): Promise<void>; update(item: ItemView): Promise<void>;
  remove(id: ItemId): void; setHidden(id: ItemId, hidden: boolean): void;
  items(): ItemView[]; occupied(t: TileXY): boolean; viewOf(id: ItemId): THREE.Object3D | undefined;
}
const layers = new WeakMap<object, ItemsLayer>();
export function itemsLayerOf(game: Game): ItemsLayer | null { return layers.get(game) ?? null; }
export function registerItemsLayer(game: Game | object, layer: ItemsLayer): void { layers.set(game, layer); }

export function createItemsLayer(group: THREE.Group, deps: { assets: Pick<AssetService, 'model'>; heightAt(p: Vec2): number }): ItemsLayer {
  const data = new Map<ItemId, ItemView>(), views = new Map<ItemId, THREE.Object3D>(), gen = new Map<ItemId, number>();
  let counter = 0;
  function place(obj: THREE.Object3D, item: ItemView): void {
    if (item.at.loc !== 'outdoors') return;
    const at = item.at;
    if (at.placed) {
      const { w, d } = footprintOf({ catalogId: item.catalogId, rot: at.rot });
      const c = { x: at.x + w / 2, y: at.y + d / 2 };
      obj.position.set(c.x, deps.heightAt(c), c.y);
      obj.rotation.y = -at.rot * Math.PI / 2;
    } else {
      const c = { x: at.x + 0.5, y: at.y + 0.5 };
      obj.position.set(c.x, deps.heightAt(c), c.y);
      obj.rotation.y = ((at.x * 7 + at.y * 13) % 8) * (Math.PI / 4);   // natural scatter, stable per tile
    }
  }
  const self: ItemsLayer = {
    async sync(items) {
      for (const id of [...data.keys()]) self.remove(id);
      await Promise.all(items.map((i) => self.add(i)));
    },
    async add(item) {
      if (item.at.loc !== 'outdoors') return;
      self.remove(item.id);
      data.set(item.id, item);
      const mine = ++counter; gen.set(item.id, mine);
      const obj = await deps.assets.model(item.catalogId, typeof item.meta?.size === 'number' ? { size: item.meta.size } : {});
      if (gen.get(item.id) !== mine) return;                          // removed or replaced while loading
      obj.userData.itemId = item.id;
      place(obj, item);
      views.set(item.id, obj);
      group.add(obj);
    },
    async update(item) {
      const v = views.get(item.id);
      if (!v || item.at.loc !== 'outdoors') return self.add(item);
      data.set(item.id, item); place(v, item);
    },
    remove(id) {
      gen.delete(id); data.delete(id);
      const v = views.get(id);
      if (v) { group.remove(v); views.delete(id); }
    },
    setHidden(id, hidden) { const v = views.get(id); if (v) v.visible = !hidden; },
    items: () => [...data.values()],
    occupied: (t) => [...data.values()].some((i) => i.at.loc === 'outdoors' && !i.at.placed && i.at.x === t.x && i.at.y === t.y),
    viewOf: (id) => views.get(id),
  };
  return self;
}
